/* Ash — homepage text float-on-scroll auto-applier
 *
 * RL behaviour: section text starts at its natural position inside the
 * section. As the user scrolls, the text appears to float anchored to
 * the bottom of the viewport, until the section's bottom edge reaches
 * the float position — at which point the text scrolls away with the
 * section.
 *
 * Mechanism (CSS): `position: sticky; bottom: var(--ash-float-bottom)`.
 * The CSS lives in assets/ash-scroll-text-rise.css and is loaded by
 * snippets/stylesheets.liquid globally. This JS only decides which
 * elements should opt in by adding the `data-ash-float` attribute.
 *
 * Opt-in rules — auto-applies to:
 *   - <h1>, <h2>, button blocks, and elements with .ash-text-block
 *   - that live inside <main>
 *   - whose enclosing .shopify-section is at least 1.5× the element's
 *     height (otherwise sticky has no room to float)
 *   - skipping carousels, sliders, drawers, headers/footers, modals,
 *     and product cards (which get cloned and would break)
 *
 * Opt-out: add `data-ash-float-skip` on any element to disable.
 * Per-element tuning: inline style `--ash-float-bottom: 32px` or
 * `--ash-float-start: 20%`.
 *
 * Re-runs after `shopify:section:load` and on resize so editor reloads
 * + responsive recalcs stay correct.
 */

(function () {
  const SKIP_ANCESTORS = [
    '.slideshow',
    '.slideshow-component',
    'slideshow-component',
    'slideshow-slide',
    '.swiper',
    'swiper-container',
    '.splide',
    '.flickity',
    '.product-list__carousel',
    '.shoppable-videos__list',
    '.scroll-snap-carousel',
    '[data-ash-no-float]',
    '[role="dialog"]',
    'dialog',
    '.cart-drawer',
    'cart-drawer-component',
    '.search-modal',
    '.header',
    'header-component',
    '.ash-mega-menu',
    '.product-card',
    '.product-grid-block',
    '.testimonial-card',
    '.fbt__item',
    'footer',
  ].join(',');

  const CANDIDATE_SELECTOR = [
    'main h1',
    'main h2',
    'main .ash-text-block',
    'main .button-block',
    'main .ash-float-target',
  ].join(',');

  /* Min ratio of section-height to element-height for float to be
   * meaningful. Lowered to 1.15 so headlines in shorter sections
   * still get the animation (was 1.5 — too strict). */
  const MIN_RATIO = 1.15;

  function isHomepage() {
    /* layout/theme.liquid renders <main data-template="{{ template }}"> */
    const main = document.querySelector('main[data-template]');
    if (main && main.getAttribute('data-template') === 'index') return true;
    /* Fallback: body class set by some themes */
    if (document.body.classList.contains('template-index')) return true;
    return false;
  }

  function shouldSkip(el) {
    if (el.hasAttribute('data-ash-float-skip')) return true;
    if (el.closest && el.closest(SKIP_ANCESTORS)) return true;
    return false;
  }

  function containerHeight(el) {
    /* Walk up to the section wrapper to compare heights. Prefer
     * .shopify-section, then any section element, then the closest
     * positioned ancestor. */
    const section =
      el.closest('.shopify-section') ||
      el.closest('section') ||
      el.parentElement;
    return section ? section.offsetHeight : 0;
  }

  function applyFloat(el) {
    if (el.hasAttribute('data-ash-float')) return;
    if (shouldSkip(el)) return;

    const sectionH = containerHeight(el);
    const elH = el.offsetHeight || 0;
    if (!sectionH || !elH) return;
    if (sectionH < elH * MIN_RATIO) return;

    el.setAttribute('data-ash-float', '');
    /* Default starting offset — pull the text DOWN to roughly the
     * center of its section so the "float to bottom" arc is visible.
     * Per-element style="--ash-float-start: 0;" override-disables it. */
    if (!el.style.getPropertyValue('--ash-float-start')) {
      el.style.setProperty('--ash-float-start', '0');
    }
  }

  function run(root) {
    if (!isHomepage()) return;
    const targets = (root || document).querySelectorAll(CANDIDATE_SELECTOR);
    targets.forEach(applyFloat);
  }

  function init() {
    run(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme-editor section reloads */
  document.addEventListener('shopify:section:load', (e) => run(e.target || document));

  /* Responsive recalc — on resize, re-evaluate eligibility (e.g. a
   * section that was tall on desktop may collapse on mobile). Don't
   * REMOVE the attribute on shrink (sticky just becomes a no-op);
   * only add where newly eligible. Debounced. */
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => run(document), 200);
  });
})();
